import { DragPayload, DropZone } from "./types";

interface DragState {
  isDragging: boolean;
  payload: DragPayload | null;
  currentDropZone: DropZone | null;
  mouseX: number;
  mouseY: number;
}

type DragStartCallback = (
  payload: DragPayload,
  mouseX: number,
  mouseY: number,
) => void;
type DragMoveCallback = (
  mouseX: number,
  mouseY: number,
  dropZone: DropZone | null,
) => void;
type DragEndCallback = (
  payload: DragPayload,
  dropZone: DropZone | null,
) => void;

class DragManager {
  private dropZones: Map<string, DropZone> = new Map();
  private state: DragState = {
    isDragging: false,
    payload: null,
    currentDropZone: null,
    mouseX: 0,
    mouseY: 0,
  };

  private onDragStartCallbacks: Set<DragStartCallback> = new Set();
  private onDragMoveCallbacks: Set<DragMoveCallback> = new Set();
  private onDragEndCallbacks: Set<DragEndCallback> = new Set();

  private boundMouseMove = this.handleMouseMove.bind(this);
  private boundMouseUp = this.handleMouseUp.bind(this);

  registerDropZone(zone: DropZone): () => void {
    this.dropZones.set(zone.id, zone);
    return () => {
      this.dropZones.delete(zone.id);
    };
  }

  startDrag(payload: DragPayload, startX: number, startY: number) {
    this.state = {
      isDragging: true,
      payload,
      currentDropZone: null,
      mouseX: startX,
      mouseY: startY,
    };

    window.addEventListener("mousemove", this.boundMouseMove);
    window.addEventListener("mouseup", this.boundMouseUp);

    this.onDragStartCallbacks.forEach((cb) => cb(payload, startX, startY));
  }

  private handleMouseMove(event: MouseEvent) {
    if (!this.state.isDragging) {
      return;
    }

    this.state.mouseX = event.clientX;
    this.state.mouseY = event.clientY;

    const newDropZone = this.findDropZoneAt(event.clientX, event.clientY);

    if (newDropZone?.id !== this.state.currentDropZone?.id) {
      this.state.currentDropZone = newDropZone;
    }

    this.onDragMoveCallbacks.forEach((cb) =>
      cb(event.clientX, event.clientY, this.state.currentDropZone),
    );
  }

  private findDropZoneAt(x: number, y: number): DropZone | null {
    let bestMatch: DropZone | null = null;

    for (const zone of this.dropZones.values()) {
      const rect = zone.element.getBoundingClientRect();

      if (
        x >= rect.left &&
        x <= rect.right &&
        y >= rect.top &&
        y <= rect.bottom
      ) {
        bestMatch = zone;
      }
    }

    return bestMatch;
  }

  private handleMouseUp(event: MouseEvent) {
    if (!this.state.isDragging) {
      return;
    }

    window.removeEventListener("mousemove", this.boundMouseMove);
    window.removeEventListener("mouseup", this.boundMouseUp);

    const { payload, currentDropZone } = this.state;

    this.state = {
      isDragging: false,
      payload: null,
      currentDropZone: null,
      mouseX: 0,
      mouseY: 0,
    };

    if (payload) {
      // A failing callback must not prevent the others (e.g. drag state
      // resets) from running.
      this.onDragEndCallbacks.forEach((cb) => {
        try {
          cb(payload, currentDropZone);
        } catch (e) {
          console.error(e);
        }
      });
    }
  }

  onDragStart(callback: DragStartCallback): () => void {
    this.onDragStartCallbacks.add(callback);
    return () => this.onDragStartCallbacks.delete(callback);
  }

  onDragMove(callback: DragMoveCallback): () => void {
    this.onDragMoveCallbacks.add(callback);
    return () => this.onDragMoveCallbacks.delete(callback);
  }

  onDragEnd(callback: DragEndCallback): () => void {
    this.onDragEndCallbacks.add(callback);
    return () => this.onDragEndCallbacks.delete(callback);
  }

  getState() {
    return { ...this.state };
  }
}

export const dragManager = new DragManager();
